import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const AgeOnOtherPlanetsCalculator = () => {
  const [inputData, setInputData] = useState({
    earthAge: '',
    results: {}
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculatePlanetaryAges = () => {
    const { earthAge } = inputData;
    
    // Validate input
    if (!earthAge) {
      setError('Please enter your age on Earth');
      return;
    }

    // Validate numeric input
    if (isNaN(earthAge)) {
      setError('Please enter a valid number for your age');
      return;
    }

    // Convert to number
    const numericEarthAge = parseFloat(earthAge);

    // Planet orbital periods relative to Earth (in Earth years)
    const planetaryOrbitalPeriods = {
      mercury: 0.240846,
      venus: 0.615197,
      mars: 1.8808158,
      jupiter: 11.862615,
      saturn: 29.447498,
      uranus: 84.016846,
      neptune: 164.79132
    };

    // Calculate ages on other planets
    const calculatedAges = {};
    for (const planet in planetaryOrbitalPeriods) {
      calculatedAges[planet] = (numericEarthAge / planetaryOrbitalPeriods[planet]).toFixed(2);
    }

    // Create visualization data
    const planetLabels = Object.keys(planetaryOrbitalPeriods).map(planet => 
      planet.charAt(0).toUpperCase() + planet.slice(1)
    );
    const ageValues = Object.values(calculatedAges);

    const chartData = {
      labels: planetLabels,
      datasets: [{
        label: 'Age on Planets',
        data: ageValues,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)',
          'rgba(255, 206, 86, 0.5)',
          'rgba(75, 192, 192, 0.5)',
          'rgba(153, 102, 255, 0.5)',
          'rgba(255, 159, 64, 0.5)',
          'rgba(199, 199, 199, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(153, 102, 255, 1)',
          'rgba(255, 159, 64, 1)',
          'rgba(199, 199, 199, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Take Earth age as input',
      `Earth Age: ${numericEarthAge} years`,
      '\nStep 2: Divide Earth age by each planet\'s orbital period',
      'Formula: Age on Planet = Earth Age / Orbital Period of Planet',
      '\nStep 3: Calculate age for each planet',
      `Mercury: ${numericEarthAge} / 0.240846 ≈ ${calculatedAges.mercury}`,
      `Venus: ${numericEarthAge} / 0.615197 ≈ ${calculatedAges.venus}`,
      `Mars: ${numericEarthAge} / 1.8808158 ≈ ${calculatedAges.mars}`,
      `Jupiter: ${numericEarthAge} / 11.862615 ≈ ${calculatedAges.jupiter}`,
      `Saturn: ${numericEarthAge} / 29.447498 ≈ ${calculatedAges.saturn}`,
      `Uranus: ${numericEarthAge} / 84.016846 ≈ ${calculatedAges.uranus}`,
      `Neptune: ${numericEarthAge} / 164.79132 ≈ ${calculatedAges.neptune}`
    ];

    setResults({
      earthAge: numericEarthAge,
      calculatedAges,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    earthAge: '30',
    calculatedAges: {
      mercury: '124.56',
      venus: '48.77',
      mars: '15.95',
      jupiter: '2.53',
      saturn: '1.02',
      uranus: '0.36',
      neptune: '0.18'
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Age on Other Planets Calculator | Space Age Converter</title>
          <meta name="description" content="Calculate your age on other planets. This calculator converts your Earth age to equivalent ages on Mercury, Venus, Mars, Jupiter, Saturn, Uranus, and Neptune based on their orbital periods." />
          <meta name="keywords" content="age on other planets calculator, space age calculator, planetary age converter, astronomy tools" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">🚀</div>
            <h1 className="text-3xl font-bold text-gray-800">Age on Other Planets Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate your age on other planets. This calculator converts your Earth age to equivalent ages on Mercury, Venus, Mars, Jupiter, Saturn, Uranus, and Neptune based on their orbital periods around the Sun.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Age on Earth (years):</label>
              <input
                type="text"
                name="earthAge"
                value={inputData.earthAge}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter your age in Earth years (e.g., 30)"
              />
            </div>
          </div>

          <button
            onClick={calculatePlanetaryAges}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Planetary Ages
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Planetary Age Calculation</h3>
                  <p className="text-lg">Earth Age: {results.earthAge} years</p>
                  <div className="mt-4 space-y-2">
                    {Object.entries(results.calculatedAges).map(([planet, age]) => (
                      <p key={planet} className="text-lg">
                        {planet.charAt(0).toUpperCase() + planet.slice(1)}: {age} years
                      </p>
                    ))}
                  </div>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Based on planetary orbital periods relative to Earth</p>
                  <p className="text-lg">1 Earth year = Orbital period of Earth around the Sun</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Age in Earth Years'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Planets'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Age on Different Planets'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for a 30-year-old:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Earth Age: {exampleData.earthAge} years</li>
                {Object.entries(exampleData.calculatedAges).map(([planet, age]) => (
                  <li key={planet}>{planet.charAt(0).toUpperCase() + planet.slice(1)}: {age} years</li>
                ))}
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Take Earth age as input (30 years)</li>
                <li>Divide by each planet's orbital period</li>
                <li>Round to two decimal places</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Age on Other Planets Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Your Earth Age:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input your age in Earth years</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>Your age on each planet in the solar system</li>
                  <li>The calculation details</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review your calculated ages on different planets</li>
                  <li>Check the calculation steps</li>
                  <li>View the visual representation of your ages</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Compare how old you'd be on different planets</li>
                <li>Understand that time passes differently on other planets due to their orbital periods</li>
                <li>Share your results with friends to compare cosmic ages</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Age on Other Planets <span className="text-blue-600">🌍</span></h2>
          <div className="text-gray-600">
            <p>
              Your age on other planets is determined by their orbital periods around the Sun. Since planets orbit the Sun at different rates, one year on Earth isn't equivalent to one year on another planet. Here's what you should know:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Orbital Period:</strong> The time it takes for a planet to complete one orbit around the Sun</li>
              <li><strong>Time Perception:</strong> Time passes at the same rate everywhere in the universe, but the way we measure years differs by planet</li>
              <li><strong>Scientific Use:</strong> Astronomers use planetary years to measure events and phenomena in our solar system</li>
              <li><strong>Fun Fact:</strong> A day on Venus is longer than a year on Venus!</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Enter your precise Earth age for most accurate results</li>
              <li><strong>Context:</strong> Remember these calculations are based on orbital periods, not relativistic time dilation</li>
              <li><strong>Education:</strong> Use this calculator as a teaching tool for astronomy concepts</li>
              <li><strong>Comparison:</strong> Compare ages across planets to understand orbital mechanics</li>
            </ul>
            <p className="italic mt-4">
              Note: These calculations are based on the orbital periods of the planets and do not account for relativistic effects or different planetary rotation periods.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AgeOnOtherPlanetsCalculator;